import React, { useEffect, useState } from 'react';
import { Tabs, Button, Dropdown, Menu } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { getOnBoardingDataThunk } from '../../../redux/slice/preOnBoarding';
import ConsentAsk from './ConsentAsk';
import BureauCheck from './BureauCheck';
import styled from 'styled-components';
import { useAuth } from "../../../context/AuthContext";
import { RiRefreshLine } from "react-icons/ri";
import { trackFeatureAction } from '../../../analytics/googleAnalytics';


const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    display: flex;
    align-items: center;
    padding: 5px 45px;
  }
  .ant-tabs-tab {
    font-size: 16px !important;
    font-weight: 500;
  }
`;

const TabContentContainer = styled.div`
  max-height: 110vh;
  overflow-y: auto;
  padding-top: 10px;
`;

const generateConsentExcelReport = (data, fileName, columns, filterDateField) => {
  const today = moment();
  const last30Days = today.subtract(30, 'days');

  const filteredData = data.filter(item => {
    const dateToCheck = moment(item[filterDateField]);
    return dateToCheck.isAfter(last30Days);
  });

  const formattedData = filteredData.map(item => {
    const row = {};
    columns.forEach(col => {
      let value = item[col.key];
      if (col.render) {
        value = col.render(value);
      }
      row[col.label] = value;
    });
    return row;
  });

  const ws = XLSX.utils.json_to_sheet(formattedData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  XLSX.writeFile(wb, `${fileName}.xlsx`);
  trackFeatureAction(
    "PreOnboarding",
    "Employee Consent",
    "download",
    {
      report: "Consent Data",
    }
  );
};
// const generateBureauExcelReport = (data, fileName, columns, filterDateField) => {
//   const today = moment();
//   const last30Days = today.subtract(30, 'days');

//   const filteredData = data.filter(item => {
//     const dateToCheck = moment(item[filterDateField]);
//     return dateToCheck.isAfter(last30Days);
//   });

//   const formattedData = filteredData.map(item => {
//     const row = {};
//     columns.forEach(col => {
//       let value = item[col.key];
//       if (col.render) {
//         value = col.render(value);
//       }
//       row[col.label] = value;
//     });
//     return row;
//   });

//   const ws = XLSX.utils.json_to_sheet(formattedData);
//   const wb = XLSX.utils.book_new();
//   XLSX.utils.book_append_sheet(wb, ws, 'Report');
//   XLSX.writeFile(wb, `${fileName}.xlsx`);
// };

const generateBureauExcelReport = (data, fileName, columns, filterDateField) => {
  const today = moment();
  const last30Days = today.subtract(30, 'days');

  // Filter data to include only items with a non-null bureau_score and within the last 30 days
  const filteredData = data.filter(item => {
    const dateToCheck = moment(item[filterDateField]);
    return dateToCheck.isAfter(last30Days) && item['bureau_score'] !== null && item['bureau_score'] !== undefined;
  });

  const formattedData = filteredData.map(item => {
    const row = {};
    columns.forEach(col => {
      let value = item[col.key];
      if (col.render) {
        value = col.render(value);
      }
      row[col.label] = value;
    });
    return row;
  });

  const ws = XLSX.utils.json_to_sheet(formattedData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  XLSX.writeFile(wb, `${fileName}.xlsx`);
  trackFeatureAction(
    "PreOnboarding",
    "Employee Bureau",
    "download",
    {
      report: "Bureau Data",
    }
  );
};

const MainOnboarding = (props) => {
  const dispatch = useDispatch();
  const { authState } = useAuth();
  const jwtBearer = authState.token;

  const onboardingData = useSelector((state) => state?.preOnBoardingSlice?.onBoardingFormdata);

  // Add loading state for refresh button
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(getOnBoardingDataThunk({ jwtBearer }));
  }, [dispatch, jwtBearer]);

  const consentColumns = [
    { label: 'Name', key: 'name' },
    { label: 'Mobile', key: 'phone' },
    { label: 'Email', key: 'email' },
    { label: 'Link Opened', key: 'link_opened', render: (value) => value ? 'Yes' : 'No' },
    { label: 'Consent Given', key: 'consent_given', render: (value) => value ? 'Yes' : 'No' },
  ];

  const bureauColumns = [
    { label: 'Name', key: 'name' },
    { label: 'Mobile', key: 'phone' },
    { label: 'PAN', key: 'pan' },
    { label: 'DOB', key: 'dob'},
    { label: 'Pincode', key: 'address' },
    { label: 'City', key: 'city' },
    { label: 'State', key: 'state' },
    { label: 'Address', key: 'address' },
    { label: 'Bureau Score', key: 'bureau_score' },
    { label: 'Bureau Generation Date', key: 'bureau_generation_date', render: (value) => moment(value).format('DD-MM-YYYY') },
  ];

  const handleReportClick = (reportType) => {
    if (reportType === 'Consent_Report') {
      generateConsentExcelReport(onboardingData, 'Consent_Report', consentColumns, 'created_at');
    } else if (reportType === 'Bureau_Report') {
      generateBureauExcelReport(onboardingData, 'Bureau_Report', bureauColumns, 'updated_at');
    }
  };

  const handleRefreshClick = () => {
    setLoading(true);
    dispatch(getOnBoardingDataThunk({ jwtBearer }))
      .finally(() => {
        setLoading(false);
      });
  };

  const menu = (
    <Menu>
      <Menu.Item key="1" onClick={() => handleReportClick('Consent_Report')}>
        Consent Data
      </Menu.Item>
      <Menu.Item key="2" onClick={() => handleReportClick('Bureau_Report')}>
        Bureau Data
      </Menu.Item>
    </Menu>
  );

  return (
    <div style={{ marginLeft: '5px', marginRight: '5px' }}>
      <StyledTabs
        defaultActiveKey="1"
        onChange={(key) => {
          const featureMap = {
            "1": "Employee Consent",
            "2": "Employee Bureau",
          };

          const feature = featureMap[key];

          if (feature) {
            trackFeatureAction(
              "PreOnboarding",
              feature,
              "open"
            );
          }
        }}
        tabBarExtraContent={
          <div style={{ display: 'flex', gap: '25px' }}>
            <Dropdown overlay={menu}>
              <Button type="primary" size='large'>
                <DownloadOutlined /> Download Data
              </Button>
            </Dropdown>
            <div>
              <Button
                type="primary"
                shape="circle"
                size="large"
                onClick={handleRefreshClick}
                style={{
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)",
                  backgroundColor: "white",
                  position: "relative",
                  padding: 0,
                  borderRadius: "50%",
                }}
              >
                {loading ? (
                  <span
                    className={loading ? "spin" : ""}
                    style={{
                      fontSize: "16px",
                      fontWeight: "900",
                      color: "blue",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                      transformOrigin: "50% 50%",
                    }}
                  >
                    <RiRefreshLine
                      style={{
                        fontSize: "40px",
                        color: "blue",
                        marginLeft: "4px",
                      }}
                      className="spin"
                    />
                  </span>
                ) : (
                  <RiRefreshLine
                    style={{
                      fontSize: "40px",
                      color: "blue",
                      marginLeft: "4px",
                    }}
                  />
                )}
              </Button>
            </div>



          </div>
        }
      >
        <Tabs.TabPane tab="Employee Consent" key="1">
          <TabContentContainer>
            <ConsentAsk />
          </TabContentContainer>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Employee Bureau" key="2">
          <TabContentContainer>
            <BureauCheck />
          </TabContentContainer>
        </Tabs.TabPane>
      </StyledTabs>
    </div>
  );
};

export default MainOnboarding;
